import { create } from 'zustand';
import {
  createsUserAccessUseCase,
  getCredSystemInfoUserUseCase,
  getCredSystemLoginTokenUseCase,
  getCredSystemUserTokenUseCase,
  sendCredsystemSmsUseCase,
  validatesCredSystemFirstAccessUseCase,
  validatesPinCodeUseCase,
} from '~/credsystem/useCases';
import { CredsystemLocalStorage } from '~/credsystem/storage/LocalStorage';
import {
  CREDSYSTEM_APP_UID,
  CREDSYSTEM_LOGIN_TOKEN,
  CREDSYSTEM_USER_DOCUMENT,
  CREDSYSTEM_USER_PASSWORD,
  CREDSYSTEM_USER_REFRESH_TOKEN,
  CREDSYSTEM_USER_SESSION_DATA,
  CREDSYSTEM_USER_TOKEN,
  CREDSYSTEM_VALIDATION_TOKEN,
} from '~/credsystem/storage/constants';
import { getRawDocument } from '~/utils/string';

interface LoginParams {
  document: string;
  birthDate: string;
}

interface ValidatePinCodeParams {
  pinCode: string;
  document: string;
  password: string;
}

interface SendSmsResult {
  error?: boolean;
  message?: string;
  ddd?: string;
  phone?: string;
}

interface ValidatePinCodeResult {
  error: boolean;
  message?: string;
}

interface LoginResult {
  error?: boolean;
  goToToken?: boolean;
  newUser?: boolean;
  message?: string;
  smsResponse?: SendSmsResult;
}

const INITIAL_STATE = {
  credsystemUserInfo: null,
  loading: false,
  document: '',
  birthDate: '',
  password: '',
};

type CredsystemAuthState = {
  credsystemUserInfo: CredsystemUserInfoType | null;
  loading: boolean;
  document: string;
  birthDate: string;
  password: string;
  init: () => Promise<void>;
  loginServiceAccount: () => Promise<boolean>;
  getIsCredsystemUser: () => Promise<boolean>;
  login: (data: LoginParams) => Promise<LoginResult>;
  sendSms: () => Promise<SendSmsResult>;
  validatePinCode: (
    data: ValidatePinCodeParams
  ) => Promise<ValidatePinCodeResult>;
  clear: () => Promise<void>;
  logout: () => Promise<void>;
};

const useCredsystemAuthStore = create<CredsystemAuthState>((set, get) => ({
  credsystemUserInfo: null,
  loading: false,
  document: '',
  birthDate: '',
  password: '',
  init: async () => {
    const [userSessionData, userToken, userRefreshToken] = await Promise.all([
      CredsystemLocalStorage.get(CREDSYSTEM_USER_SESSION_DATA),
      CredsystemLocalStorage.get(CREDSYSTEM_USER_TOKEN),
      CredsystemLocalStorage.get(CREDSYSTEM_USER_REFRESH_TOKEN),
    ]);

    if (userSessionData && userToken && userRefreshToken) {
      set({
        credsystemUserInfo: {
          userExist: !!userSessionData,
          token: userToken,
          refreshToken: userRefreshToken,
        },
      });
    } else {
      get().clear();
    }
  },
  loginServiceAccount: async () => {
    const CredsystemToken = await getCredSystemLoginTokenUseCase();

    if (CredsystemToken) {
      await CredsystemLocalStorage.set(CREDSYSTEM_LOGIN_TOKEN, CredsystemToken);
      return true;
    } else {
      return false;
    }
  },
  getIsCredsystemUser: async () => {
    set({ loading: true });

    const validationResponse = await validatesCredSystemFirstAccessUseCase({
      document: get().document,
      password: '1900-01-01',
    });

    if (validationResponse && validationResponse.httpStatusCode === 400) {
      if (validationResponse.data.retorno.codigo === '119') {
        //new user redirects to credsystem consession contract
        set({ loading: false });
        return false;
      } else if (validationResponse.data.retorno.codigo === '911') {
        //redirects to form
        set({ loading: false });
        return true;
      } else {
        //redirects to form
        set({ loading: false });
        return false;
      }
    }

    set({ loading: false });
    return false;
  },
  login: async ({ document, birthDate }: LoginParams): Promise<LoginResult> => {
    set({ loading: true });

    const rawDocument = getRawDocument(document);

    const formattedBirthDate = [
      birthDate.split('/').reverse().join(''),
      birthDate.split('/').reverse().join('-'),
    ];

    set({ document: rawDocument });
    set({ birthDate: birthDate });
    set({ password: formattedBirthDate[0] });

    await CredsystemLocalStorage.multiSet([
      [CREDSYSTEM_USER_DOCUMENT, rawDocument],
      [CREDSYSTEM_USER_PASSWORD, formattedBirthDate[0] || ''],
    ]);

    //LoginServiceAccount gets access token
    // /auth/realms/marisa/protocol/openid-connect/token
    const CredsystemToken = await getCredSystemLoginTokenUseCase();

    if (CredsystemToken) {
      await CredsystemLocalStorage.set(CREDSYSTEM_LOGIN_TOKEN, CredsystemToken);
    } else {
      set({ loading: false });
      return {
        error: true,
        message: 'Ocorreu um erro interno, tente novamente mais tarde.',
      };
    }

    //InfoUser -> document as a param (search=${document}) and uses LoginServiceAccount return as authz token
    // /auth/admin/realms/marisa/users?search=15721764821
    const userExist = await getCredSystemInfoUserUseCase({
      document: rawDocument,
    });

    //has access?
    if (userExist) {
      //yes
      //Login /auth/realms/marisa/protocol/openid-connect/token
      const loginResponse = await getCredSystemUserTokenUseCase({
        document: rawDocument,
        password: formattedBirthDate[0] || '',
      });

      if (loginResponse?.token && loginResponse?.refreshToken) {
        await CredsystemLocalStorage.multiSet([
          [CREDSYSTEM_USER_TOKEN, loginResponse.token],
          [CREDSYSTEM_USER_REFRESH_TOKEN, loginResponse.refreshToken],
          [CREDSYSTEM_USER_SESSION_DATA, JSON.stringify(userExist)],
        ]);

        set({
          credsystemUserInfo: {
            userExist: userExist,
            token: loginResponse.token,
            refreshToken: loginResponse.refreshToken,
          },
        });
        set({ loading: false });
        return { error: false };
      }

      if (loginResponse?.message === 'Invalid user credentials') {
        set({ loading: false });
        return { error: true, message: 'Credenciais inválidas' };
      }

      if (loginResponse?.code && parseInt(loginResponse.code, 10) >= 500) {
        set({ loading: false });
        return { error: true, message: 'Houve um problema ao tentar acessar.' };
      }

      //new device?
      if (loginResponse?.code === '401' && loginResponse.changeDevice) {
        //yes
        if (loginResponse.validationToken) {
          await CredsystemLocalStorage.set(
            CREDSYSTEM_VALIDATION_TOKEN,
            loginResponse.validationToken
          );
        } else {
          set({ loading: false });
          return {
            error: true,
            message: 'Não foi possível validar seus dados.',
          };
        }

        const sendSmsResponse = await sendCredsystemSmsUseCase();

        if (sendSmsResponse) {
          set({ loading: false });
          return {
            goToToken: true,
            smsResponse: {
              message: sendSmsResponse.data.mensagem,
              ddd: sendSmsResponse.data.ddd,
              phone: sendSmsResponse.data.ultimosDigitos,
            },
          };
        } else {
          set({ loading: false });
          return {
            error: true,
            message: 'Não foi possível enviar o seu código de validação.',
          };
        }
      }
    } else {
      //no
      //check data /api/v1/identificacao/dados

      const apiResponse = await validatesCredSystemFirstAccessUseCase({
        document: rawDocument,
        password: formattedBirthDate[1] || '',
      });

      if (apiResponse && apiResponse.httpStatusCode === 400) {
        if (apiResponse.data.retorno.codigo === '119') {
          //new user redirects to credsystem consession contract
          set({ loading: false });
          return { newUser: true, message: 'Tela de concessão' };
        } else if (apiResponse.data.retorno.codigo === '911') {
          //redirects to form
          set({ loading: false });
          return {
            error: true,
            message: 'Credenciais inválidas.',
          };
        } else {
          //redirects to form
          set({ loading: false });
          return {
            error: true,
            message: 'Ocorreu um erro na validação dos seus dados.',
          };
        }
      } else {
        const credsystemToken = apiResponse?.data.access_token;

        if (credsystemToken) {
          await CredsystemLocalStorage.set(
            CREDSYSTEM_VALIDATION_TOKEN,
            credsystemToken
          );
        } else {
          return {
            error: true,
            message: 'Não foi possível validar seus dados.',
          };
        }

        const sendSmsResponse = await sendCredsystemSmsUseCase();

        if (sendSmsResponse) {
          set({ loading: false });
          return {
            goToToken: true,
            smsResponse: {
              message: sendSmsResponse.data.mensagem,
              ddd: sendSmsResponse.data.ddd,
              phone: sendSmsResponse.data.ultimosDigitos,
            },
          };
        }

        set({ loading: false });
        return {
          error: true,
          message: 'Não foi possível enviar o seu código de validação.',
        };
      }
    }

    return {
      error: true,
      message: 'Ocorreu um erro. Por favor, Tente novamente mais tarde.',
    };
  },
  sendSms: async (): Promise<SendSmsResult> => {
    set({ loading: true });
    const sendSmsResponse = await sendCredsystemSmsUseCase();

    set({ loading: false });

    return {
      message: sendSmsResponse?.data.mensagem,
      ddd: sendSmsResponse?.data.ddd,
      phone: sendSmsResponse?.data.ultimosDigitos,
    };
  },
  validatePinCode: async ({
    pinCode,
    document,
    password,
  }: ValidatePinCodeParams): Promise<ValidatePinCodeResult> => {
    set({ loading: true });
    const pinCodeValidationResponse = await validatesPinCodeUseCase({
      pinCode: pinCode,
    });

    if (!pinCodeValidationResponse) return { error: true };

    const newAccess = await createsUserAccessUseCase({
      password: password,
    });

    if (!newAccess) {
      set({ loading: false });
      return { error: true };
    }

    const loginResponse = await getCredSystemUserTokenUseCase({
      document: document,
      password: password,
    });

    if (loginResponse?.token && loginResponse?.refreshToken) {
      await CredsystemLocalStorage.multiSet([
        [CREDSYSTEM_USER_TOKEN, loginResponse.token],
        [CREDSYSTEM_USER_REFRESH_TOKEN, loginResponse.refreshToken],
        [CREDSYSTEM_USER_SESSION_DATA, JSON.stringify(true)],
      ]);

      set((currentState) => ({
        credsystemUserInfo: {
          ...currentState.credsystemUserInfo,
          token: loginResponse.token,
          refreshToken: loginResponse.refreshToken,
        },
      }));
      set({ loading: false });
      return { error: false };
    } else {
      set({ loading: false });
      return { error: true, message: 'Houve um problema na autenticação' };
    }
  },
  clear: async () => {
    set(INITIAL_STATE);
    await CredsystemLocalStorage.multiRemove([
      CREDSYSTEM_VALIDATION_TOKEN,
      CREDSYSTEM_USER_SESSION_DATA,
      CREDSYSTEM_USER_TOKEN,
      CREDSYSTEM_USER_REFRESH_TOKEN,
      CREDSYSTEM_LOGIN_TOKEN,
      CREDSYSTEM_USER_DOCUMENT,
      CREDSYSTEM_USER_PASSWORD,
    ]);
  },
  logout: async () => {
    await CredsystemLocalStorage.remove(CREDSYSTEM_APP_UID);
    await get().clear();
  },
}));

export default useCredsystemAuthStore;
